import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, setForceLogoutHandler } from '../services/api';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminAccessToken');
      const refreshToken = localStorage.getItem('adminRefreshToken');
      if (token || refreshToken) {
        try {
          // Verify token is still valid by hitting admin stats endpoint
          // The axios interceptor will auto-refresh if the access token is expired
          const response = await api.get('/admin/stats');
          if (response.data.success) {
            const userInfo = localStorage.getItem('adminUser');
            if (userInfo) {
              setUser(JSON.parse(userInfo));
            }
          }
        } catch (err) {
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            // Genuine auth failure — clear tokens
            localStorage.removeItem('adminAccessToken');
            localStorage.removeItem('adminRefreshToken');
            localStorage.removeItem('adminUser');
          } else {
            // Network error / server restarting — keep tokens, trust localStorage
            console.warn('Auth check failed (network), keeping session:', err.message);
            const userInfo = localStorage.getItem('adminUser');
            if (userInfo) {
              setUser(JSON.parse(userInfo));
            }
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password, totpCode, tempToken, rememberDevice) => {
    try {
      setError(null);

      // Step 2: TOTP verification (if we have a temp token)
      if (totpCode && tempToken) {
        const response = await authAPI.verifyTOTP(tempToken, totpCode, rememberDevice);
        const { tokens, user: userData, deviceToken } = response.data;

        localStorage.setItem('adminAccessToken', tokens.accessToken);
        localStorage.setItem('adminRefreshToken', tokens.refreshToken);
        localStorage.setItem('adminUser', JSON.stringify(userData));

        // Store device trust token if returned
        if (deviceToken) {
          localStorage.setItem('adminDeviceToken', deviceToken);
        }

        setUser(userData);
        return { success: true };
      }

      // Step 1: Admin login — send device token if we have one
      const storedDeviceToken = localStorage.getItem('adminDeviceToken') || undefined;
      const response = await authAPI.adminLogin(email, password, storedDeviceToken);

      // If TOTP required, return that to the caller
      if (response.data.requiresTOTP) {
        return {
          success: false,
          requiresTOTP: true,
          tempToken: response.data.tempToken,
        };
      }

      // No TOTP — complete login (trusted device or TOTP disabled)
      const { tokens, user: userData } = response.data;

      localStorage.setItem('adminAccessToken', tokens.accessToken);
      localStorage.setItem('adminRefreshToken', tokens.refreshToken);
      localStorage.setItem('adminUser', JSON.stringify(userData));
      setUser(userData);

      return { success: true };
    } catch (err) {
      const data = err.response?.data || {};
      const message = data.error || 'Login failed';
      setError(message);
      return { success: false, error: message, warning: data.warning || null };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminAccessToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  // Register logout handler so API interceptors use React state instead of hard redirects
  useEffect(() => {
    setForceLogoutHandler(() => {
      setUser(null);
    });
    return () => setForceLogoutHandler(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
